// ── BILLING: SHARED HELPERS ────────────────────────────────────────────
// Program pricing helpers, the "add/update card on file" (Stripe) flow, the
// invoice-style charge-history renderer, and the charge-declined error
// display — all read by more than one domain, so they live here rather than
// in parent-billing.js. Every consumer (parent-dashboard.js, admin-settings.js,
// admin-student-actions.js, admin-students.js, parent-classes.js,
// parent-reenroll.js, admin-parent-history.js, parent-billing.js, etc.) does
// a real import. cardModal/saveCard/cardModalForUser/payNow/downloadReceipt
// are no longer window-exposed (phase 2 of the onclick → addEventListener
// migration) — cardModal's Save button and renderChargeHistory's Receipt
// buttons wire their own listeners directly (imperative functions with full
// control over the DOM they just wrote); cardModalForUser/payNow are real
// imports wired via each consumer view's own VIEW_INIT.
//
// esc (shared classic script) stays a bare global — not migrated yet.
import { apiFetch } from '../core/api.js';
import { DB, PROGRAMS, session } from '../core/state.js';
import { fmtD, TODAY } from '../core/format.js';
import { childName, enrollmentMonthlyRate, hasRateOverride } from '../core/domain-utils.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { refresh } from '../core/routing.js';

// ── Program pricing ─────────────────────────────────────────────────
// BACKEND RULES for the scheduled billing job (no client-side run exists in this prototype):
//   • Monthly run (1st of each month): for each active enrollment with billingMode === 'monthly',
//     charge that program's monthly rate if the current month is in its seasonMonths. SKIP any
//     enrollment whose billingMode is 'advance' or 'onetime' — those are paid in full already.
//   • Scheduled advance run (start of each semester): for each active enrollment with
//     billingMode === 'advance', charge that semester's full total at its startMonth. PC produces
//     two charges/season (Sep & Jan); BB produces one (Sep). The family chose advance once at
//     enrollment and is never re-prompted; the second-semester PC charge fires automatically.
//   • A dropped enrollment is excluded from both runs.
// isProgram is false for Tap/Voice classes by design (it gates PC/BB-only UI),
// but they still bill monthly and should count toward a monthly-tuition total.
export function programMonthly(p) { return p && (p.isProgram || p.workshopGroup === 'tap_voice') ? (p.monthly || 0) : 0; }
// Human-readable price label: "$100/mo" for programs, "$320" for one-time classes.
// Tap/Voice classes (billingType 'workshop', workshopGroup 'tap_voice') bill
// monthly like PC/BB — only Summer Workshops (workshopGroup 'summer') are a
// true flat one-time price. See ProgramRoutes::formatProgram() for why price
// is always 0 on anything that bills monthly.
export function priceLabel(p) {
  if (!p) return '';
  if (p.billingType === 'onetime' || (p.billingType === 'workshop' && p.workshopGroup !== 'tap_voice')) {
    return fmt$(programOneTimePrice(p));
  }
  return p.monthly ? fmt$(p.monthly) + '/mo' : 'No fee';
}
export function programOneTimePrice(p) { return p && (p.billingType === 'onetime' || p.billingType === 'workshop') ? (p.price || 0) : 0; }
// priceLabel for one performer's enrollment rather than for the program.
//
// Identical to priceLabel() except where that performer carries a reduced rate,
// which is what the billing run will actually charge them. Use this anywhere a
// specific performer's tuition is shown -- the performer detail page, a
// family's dashboard -- and plain priceLabel() for the catalog and Settings,
// where the program's own price is the right answer.
export function enrollmentPriceLabel(p, performerId) {
  if (!p || !performerId) return priceLabel(p);
  if (!programMonthly(p) || !hasRateOverride(performerId, p.id)) return priceLabel(p);
  return fmt$(enrollmentMonthlyRate(performerId, p.id)) + '/mo';
}
// Full season total for a program (monthly × number of season months). No prepay discount.
export function programSeasonTotal(p) { return p && p.isProgram && p.seasonMonths ? p.monthly * p.seasonMonths.length : 0; }
// The semester that contains a given month (1–12), or null.
export function semesterForMonth(p, month) {
  if (!p || !p.semesters) return null;
  return p.semesters.find(s => s.months.includes(month)) || null;
}
// Total for one semester of a program.
// The next semester due to start, for a program whose season is NOT currently underway.
// Used by the pre-season advance switch: there's no in-progress unit to charge a remainder
// on, but the upcoming unit will be billed in full by advance_billing.php at its startMonth.
// Distance is measured forward around the calendar, so May → Fall (Sep) picks Fall, and a
// semester starting this very month is excluded (that's currentSemester's job, not this).
export function upcomingSemester(p, date) {
  if (!p || !p.semesters || !p.semesters.length) return null;
  const m = date.getMonth() + 1;
  let best = null;
  let bestDistance = 13;
  for (const s of p.semesters) {
    const start = s.startMonth ?? (s.months && s.months[0]);
    if (!start) continue;
    const distance = (start - m + 12) % 12;
    if (distance === 0) continue;
    if (distance < bestDistance) { bestDistance = distance; best = s; }
  }
  return best;
}
// Which semester is "current" for billing as of a given date.
export function currentSemester(p, date) {
  const m = date.getMonth() + 1;
  return semesterForMonth(p, m);
}

// ── Card on file (Stripe) ───────────────────────────────────────────
export const CARD_LOGOS = {
  Visa:       `<div style="background:#1a1f71;border-radius:4px;padding:3px 7px;font-size:11px;font-weight:800;color:#fff;font-family:Arial,sans-serif;letter-spacing:0.02em">VISA</div>`,
  Mastercard: `<div style="display:flex;align-items:center;gap:0"><div style="width:20px;height:20px;border-radius:50%;background:#eb001b;opacity:0.95"></div><div style="width:20px;height:20px;border-radius:50%;background:#f79e1b;margin-left:-10px;opacity:0.95"></div></div>`,
  Amex:       `<div style="background:#2557d6;border-radius:4px;padding:3px 6px;font-size:9px;font-weight:700;color:#fff;font-family:Arial,sans-serif;letter-spacing:0.03em;white-space:nowrap">AMEX</div>`,
  Discover:   `<div style="background:#fff;border:1px solid #e0e0e0;border-radius:4px;padding:3px 5px;display:flex;align-items:center;gap:4px"><span style="font-size:9px;font-weight:700;color:#231f20;font-family:Arial,sans-serif">DISCOVER</span><div style="width:12px;height:12px;border-radius:50%;background:#f76f20;flex-shrink:0"></div></div>`
};

let stripeInstance = null;
let stripeElements = null;
let stripeCardElement = null;
let cardTargetUserId = null;

// The outstanding balance saving this card will settle. Only ever non-zero
// for a parent saving their own card — that's the one case the backend pays
// on save (see BillingCardRoutes::confirmCard), and the modal must say so before
// they type a card number rather than surprise them with a charge after.
function dueBalanceOnSave(targetId) {
  if (targetId !== session.id) return 0;
  return DB.charges
    .filter(c => c.accountId === session.id && c.status === 'due')
    .reduce((sum, c) => sum + c.amount, 0);
}

export async function cardModal() {
  const targetId = cardTargetUserId || session.id;
  const target = cardTargetUserId ? DB.users.find(x => x.id === cardTargetUserId) : session;
  const hasCard = !!(target && target.card);
  const dueOnSave = dueBalanceOnSave(targetId);

  // Prepare Stripe before opening modal
  try {
    if (!stripeInstance) {
      const config = await apiFetch('/stripe/config');
      if (!config.publishableKey) throw new Error('Stripe is not configured yet.');
      stripeInstance = Stripe(config.publishableKey);
    }
    const setupData = await apiFetch('/stripe/setup-intent', {
      method: 'POST', body: JSON.stringify({ accountId: targetId })
    });

    openModal(`
      <h3>${hasCard ? 'Update card' : 'Add a card'}</h3>
      <p class="card-intro">Card details are handled securely by Stripe — they never touch our servers.</p>
      <div id="stripe-card-element" style="border-radius:7px;min-height:300px;margin-bottom:14px"></div>
      ${dueOnSave > 0 ? `<div style="background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.3);border-radius:7px;padding:11px 13px;margin-bottom:12px;font-size:13px;color:var(--cream);line-height:1.5">
        You have an outstanding balance of <strong>${fmt$(dueOnSave)}</strong>. Saving this card will pay it now.
      </div>` : ''}
      <span class="error-msg" id="cdErr"></span>
      <button class="btn" id="saveCardBtn" style="margin-top:6px">${dueOnSave > 0 ? `Save card &amp; pay ${fmt$(dueOnSave)}` : 'Save card'}</button>`);
    const appearance = {
      theme: 'night',
      variables: {
        colorPrimary: '#c9a84c',
        colorBackground: 'rgba(255,255,255,0.06)',
        colorText: '#f0ebe0',
        colorTextSecondary: '#9e9488',
        colorDanger: '#f87171',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSizeBase: '14px',
        borderRadius: '7px',
        spacingUnit: '4px',
      },
      rules: {
        '.Input': { border: '1px solid rgba(255,255,255,0.08)', padding: '10px 13px' },
        '.Input:focus': { borderColor: '#c9a84c', boxShadow: 'none' },
        '.Label': { fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9e9488', fontWeight: '500' },
      }
    };
    stripeElements = stripeInstance.elements({ clientSecret: setupData.clientSecret, appearance });
    stripeCardElement = stripeElements.create('payment');
    stripeCardElement.mount('#stripe-card-element');
    const saveCardBtn = document.getElementById('saveCardBtn');
    saveCardBtn.dataset.clientSecret = setupData.clientSecret;
    saveCardBtn.dataset.accountId = targetId;
    saveCardBtn.dataset.label = saveCardBtn.textContent;
    saveCardBtn.addEventListener('click', saveCard);
  } catch (e) {
    toastError(e.message || 'Could not load card form.');
  }
}

async function saveCard() {
  const btn = document.getElementById('saveCardBtn');
  const err = document.getElementById('cdErr');
  const clientSecret = btn.dataset.clientSecret;
  const accountId = btn.dataset.accountId;

  if (!stripeElements || !clientSecret) {
    if (err) { err.textContent = 'Card form not ready. Please close and try again.'; err.classList.add('show'); }
    return;
  }

  const label = btn.dataset.label || 'Save card';
  const paying = String(accountId) === String(session.id) &&
    DB.charges.some(c => c.accountId === session.id && c.status === 'due');

  btn.disabled = true; btn.textContent = paying ? 'Saving & paying…' : 'Saving…';
  if (err) err.classList.remove('show');

  const { error, setupIntent } = await stripeInstance.confirmSetup({
    elements: stripeElements,
    redirect: 'if_required',
  });

  if (error) {
    btn.disabled = false; btn.textContent = label;
    if (err) { err.textContent = error.message; err.classList.add('show'); }
    return;
  }

  try {
    const result = await apiFetch('/stripe/confirm-card', {
      method: 'POST',
      body: JSON.stringify({ accountId, paymentMethodId: setupIntent.payment_method })
    });
    const cardTarget = cardTargetUserId ? DB.users.find(x => x.id === cardTargetUserId) : session;
    if (cardTarget) cardTarget.card = result.card;
    cardTargetUserId = null;
    stripeElements = null; stripeCardElement = null;

    // The backend settles the balance on save (parent's own card only) and
    // reports that attempt separately from the save. A decline is NOT a failed
    // save — the card is on file either way — so the modal closes and the
    // payment result is what the toast talks about.
    const payment = result.payment;
    closeModal(); refresh();
    if (payment && payment.ok && payment.charged > 0) {
      DB.charges.filter(c => c.accountId === session.id && c.status === 'due').forEach(c => c.status = 'paid');
      refresh();
      toast(`Card saved — ${fmt$(payment.charged)} paid. Thank you!`);
    } else if (payment && !payment.ok) {
      toastError(payment.error || 'Card saved, but the payment was declined.');
    } else {
      toast('Card saved');
    }
  } catch (e) {
    btn.disabled = false; btn.textContent = label;
    if (err) { err.textContent = e.message || 'Could not save card.'; err.classList.add('show'); }
  }
}

export function cardModalForUser(userId) {
  cardTargetUserId = userId;
  cardModal();
}

// ── Pay-now (parent) ────────────────────────────────────────────────
export async function payNow() {
  const btn = document.getElementById('payNowBtn');
  if (btn) {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.style.opacity = '0.7';
    btn.style.cursor = 'wait';
    btn.textContent = 'Processing payment…';
  }
  try {
    await apiFetch('/charges/pay', { method: 'POST' });
    DB.charges.filter(c => c.accountId === session.id && c.status === 'due').forEach(c => c.status = 'paid');
    refresh();
    toast('Payment complete — thank you!');
  } catch (e) {
    if (btn) { btn.disabled = false; btn.style.opacity = '1'; btn.style.cursor = 'pointer'; btn.textContent = 'Pay now'; }
    toastError(e.message || 'Payment failed. Please try again.');
  }
}

// ── Charge history (shared invoice-style renderer) ─────────────────
// Used by BOTH the parent payment-history page (parent-billing.js) and the
// admin per-parent history page (admin-parent-history.js's a-parent-history)
// — single source of truth so the two surfaces can never visually drift.
export function phSeasonRange(season) {
  const y = TODAY.getFullYear();
  const m = TODAY.getMonth() + 1; // 1-based
  // Current season = Sep of current or previous year through end of Apr
  const seasonStartYear = m >= 9 ? y : y - 1;
  if (season === 'current') return { from: `${seasonStartYear}-09-01`, to: `${seasonStartYear + 1}-04-30` };
  if (season === 'last')    return { from: `${seasonStartYear - 1}-09-01`, to: `${seasonStartYear}-04-30` };
  return { from: '2000-01-01', to: '2099-12-31' }; // all time
}

// Callers pass the account, the filter state, and the target element.
export function renderChargeHistory({ accountId, season, status, targetId }) {
  const { from, to } = phSeasonRange(season);
  const rows = DB.charges.filter(c =>
    c.accountId === accountId && c.date >= from && c.date <= to && (!status || c.status === status)
  ).slice().sort((a,b) => b.date.localeCompare(a.date));

  const el = document.getElementById(targetId);
  if (!el) return;
  if (!rows.length) { el.innerHTML = '<p class="empty">No charges match these filters.</p>'; return; }

  const byDate = {};
  rows.forEach(c => { (byDate[c.date] = byDate[c.date] || []).push(c); });
  const invoices = Object.entries(byDate).sort((a,b) => b[0].localeCompare(a[0]));

  el.innerHTML = invoices.map(([date, irows]) => {
    const total   = irows.reduce((s,c) => s + c.amount, 0);
    const allSame = irows.every(r => r.status === irows[0].status);
    const st      = allSame ? irows[0].status : 'mixed';
    const pillCls = st === 'paid' ? 'pill-paid' : st === 'due' ? 'pill-due' : st === 'refunded' ? 'pill-refunded' : 'pill-off';
    const pillLbl = st === 'mixed' ? 'partial' : st;
    return `
    <div style="padding:10px 0;border-bottom:1px solid var(--border-soft)">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
        <div style="display:flex;align-items:center;gap:10px">
          <span style="font-size:13px;color:var(--cream);font-weight:500">${fmtD(date)}</span>
          <span class="pill ${pillCls}" style="font-size:10px">${pillLbl}</span>
        </div>
        <div style="display:flex;align-items:center;gap:12px">
          <span class="amt" style="font-size:15px${irows.some(c => c.status === 'due') ? ';color:var(--red)' : ''}">${fmt$(total)}</span>
          ${st !== 'due' ? `<button data-action="download-receipt" data-date="${date}" style="font-size:11px;color:var(--gold);background:none;border:none;cursor:pointer;padding:0;white-space:nowrap;font-family:inherit;flex-shrink:0">Receipt &darr;</button>` : ''}
        </div>
      </div>
      ${irows.map(c => {
        const nm = c.childId ? childName(c.childId) : '';
        const prog = (PROGRAMS.find(p => p.id === c.programId) || {}).name;
        const label = nm ? (prog ? `${esc(nm)} (${esc(prog)})` : esc(nm)) : esc(c.desc);
        return `
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px;padding:3px 0 3px 10px;border-left:2px solid var(--border-soft)">
          <div style="font-size:12px;color:var(--cream);min-width:0">${label}</div>
          <span style="font-size:12px;color:var(--gold-light);flex-shrink:0">${fmt$(c.amount)}</span>
        </div>`;
      }).join('')}
    </div>`;
  }).join('');
  el.querySelectorAll('[data-action="download-receipt"]').forEach(btn => {
    btn.addEventListener('click', () => downloadReceipt(accountId, btn.dataset.date));
  });
}

export function downloadReceipt(accountId, date) {
  const u = DB.users.find(x => x.id === accountId) || session;
  // All charges in this transaction = same account + same date
  const rows = DB.charges.filter(c => c.accountId === accountId && c.date === date)
    .filter(c => c.status !== 'due');
  if (!rows.length) return;
  const total = rows.reduce((s, c) => s + c.amount, 0);
  const allSame = rows.every(r => r.status === rows[0].status);
  const status = allSame ? rows[0].status : 'mixed';
  const statusLabel = status === 'paid' ? 'Paid' : status === 'refunded' ? 'Refunded' : status === 'mixed' ? 'Partial' : 'Due';
  const statusColor = status === 'paid' ? '#34d399' : status === 'refunded' ? '#9e9488' : status === 'mixed' ? '#c9a84c' : '#f87171';
  // Stripe reference — shared across the transaction
  const stripeRef = rows.find(c => c.stripeRef)?.stripeRef || null;
  // Card the payment actually used, recorded on the charge when it settled — not
  // the card on file now, which is a different thing once a family swaps cards.
  // Null for anything settled outside Stripe and for old rows the backfill could
  // not resolve; the receipt just drops the line rather than guessing.
  const cardRow   = rows.find(c => c.cardLast4);
  const cardLabel = cardRow ? `${cardRow.cardBrand || 'Card'} ending ${cardRow.cardLast4}` : null;
  const lineRows = rows.map(c => {
    const ch = DB.children.find(x => x.id === c.childId);
    return `<tr>
      <td style="padding:10px 0;border-bottom:1px solid #e8e4dc;font-size:14px;color:#1a1a2e">
        ${esc(c.desc)}${ch ? `<div style="font-size:12px;color:#9e9488;margin-top:1px">${esc(ch.name)}</div>` : ''}
      </td>
      <td style="padding:10px 0;border-bottom:1px solid #e8e4dc;font-size:14px;color:#1a1a2e;text-align:right;white-space:nowrap">${fmt$(c.amount)}</td>
    </tr>`;
  }).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self'; script-src 'self' 'unsafe-inline' https://js.stripe.com; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src https://fonts.gstatic.com; connect-src 'self' https://fonts.googleapis.com https://api.stripe.com; frame-src https://js.stripe.com; img-src 'self' data: https://*.stripe.com;">
<title>Receipt — ${fmtD(date)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Source Sans 3', sans-serif; font-weight: 300; color: #1a1a2e; background: #fff; padding: 48px; max-width: 560px; margin: 0 auto; }
  .logo { font-family: 'Libre Baskerville', serif; font-size: 22px; font-weight: 700; color: #1a1a2e; margin-bottom: 4px; }
  .logo em { font-style: italic; color: #c9a84c; }
  .tagline { font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: #9e9488; margin-bottom: 36px; }
  h1 { font-family: 'Libre Baskerville', serif; font-size: 26px; font-weight: 700; margin-bottom: 6px; }
  .receipt-id { font-size: 12px; color: #9e9488; margin-bottom: 32px; }
  .section { margin-bottom: 24px; }
  .label { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: #9e9488; font-weight: 500; margin-bottom: 4px; }
  .value { font-size: 15px; color: #1a1a2e; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  .total-row { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; }
  .total-label { font-size: 15px; color: #1a1a2e; }
  .total-value { font-family: 'Libre Baskerville', serif; font-size: 32px; font-weight: 700; color: #1a1a2e; }
  .status { display: inline-block; padding: 3px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; border: 1px solid ${statusColor}; color: ${statusColor}; }
  .footer { margin-top: 48px; font-size: 11px; color: #9e9488; line-height: 1.8; }
  @media print { body { padding: 24px; } }
</style></head><body>
  <div class="logo">Center Stage <em>Youth Performers</em></div>
  <div class="tagline">csyp.org</div>
  <h1>Receipt</h1>
  <div class="receipt-id">${fmtD(date)}</div>
  <div class="section">
    <div class="label">Billed to</div>
    <div class="value">${esc(u.firstName)} ${esc(u.lastName)}</div>
    <div class="value" style="font-size:13px;color:#9e9488">${esc(u.email)}</div>
  </div>
  <div class="section">
    <div class="label">Items</div>
    <table>${lineRows}</table>
  </div>
  ${cardLabel ? `<div class="section">
    <div class="label">Payment method</div>
    <div class="value">${esc(cardLabel)}</div>
  </div>` : ''}
  <div class="total-row">
    <div>
      <div class="total-label">Total</div>
      <div style="margin-top:6px"><span class="status">${statusLabel}</span></div>
    </div>
    <div class="total-value">${fmt$(total)}</div>
  </div>
  <div class="footer">
    Questions? Contact us at lori@csyp.org${stripeRef ? '<br>Payment reference: ' + esc(stripeRef) : ''}
  </div>
<script><\/script>
</body></html>`;
  const w = window.open('', '_blank', 'width=640,height=820');
  w.document.write(html);
  w.document.close();
}

// ── Charge-declined error display ───────────────────────────────────
// Shown inside whatever modal is currently open after a failed charge
// attempt; used by every checkout/charge flow (parent Re-enroll, Classes,
// Store, and the admin Dashboard's "Charge card" action).
export function showChargeError(msg) {
  const box = document.getElementById('modalBox');
  let err = document.getElementById('chargeErr');
  if (!err) {
    err = document.createElement('p');
    err.id = 'chargeErr';
    err.style.cssText = 'font-size:13px;color:var(--red);margin-top:14px;padding:10px 12px;background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.25);border-radius:7px;line-height:1.5';
    box.appendChild(err);
  }
  err.textContent = msg;
  // Re-enable the confirm button so admin can retry
  const btn = box.querySelector('.btn:not(.btn-ghost)');
  if (btn) { btn.disabled = false; btn.textContent = btn.textContent.replace('Processing…', btn.dataset.label || 'Retry'); }
}
